import { matchPath } from 'react-router-dom'

import {
  findMenuEntry,
  buildMenuPath,
  isPathAllowedInGlobalView,
  isPathAllowedInSuperFeed,
} from './menuRegistry'
import { THREAT_INTEL_CUST_LICENSE_CODE, THREAT_INTEL_CUST_VALID_PATHS } from './threatIntelCustomerUtils'

// ===========================================================================
// Per-persona landing page (D-13, PIXMC-6521).
//
// Three build-time env vars name the menuId each persona lands on after login:
//
//   REACT_APP_LANDING_MENU_PIXMADMIN="incidentFeed.overview"
//   REACT_APP_LANDING_MENU_TENANTADMIN=""
//   REACT_APP_LANDING_MENU_CUSTOMERADMIN=""
//
// Blank, absent, unknown or not-permitted → this resolver returns null and
// App.getDefaultPath falls through to its original body, so a build with all three blank
// behaves exactly as it did before this change.
//
// ⚠️ Why guards rather than a bare redirect. Four existing mechanisms will fight a
// naively-configured landing page, and TWO of them produce an unrecoverable redirect
// loop rather than a visible error:
//
//   1. auth.js `userIsPixmAdmin` bounces a non-PA user to '/', which re-enters the
//      landing decision — which sends them back. Guarded by the registry's `roles`.
//   2. ThreatIntelCustomerRedirect is mounted OUTSIDE the <Switch> (App.js), so for a
//      TISOC CustomerAdmin it overrides any path outside THREAT_INTEL_CUST_VALID_PATHS
//      on every navigation. Guarded by isAllowedForLicense below.
//   3. Global View blocks GLOBAL_VIEW_DISABLED_PATHS (navbar bounces to /incidents).
//   4. Super Feed blocks SUPER_FEED_DISABLED_PATHS.
//
// The guard list is not optional. Removing any one of these turns a typo in an env file
// into a login loop that cannot be escaped from inside the app.
// ===========================================================================

const LANDING_ENV_VAR_BY_ROLE = {
  PixmAdmin: 'REACT_APP_LANDING_MENU_PIXMADMIN',
  TenantAdmin: 'REACT_APP_LANDING_MENU_TENANTADMIN',
  CustomerAdmin: 'REACT_APP_LANDING_MENU_CUSTOMERADMIN',
}

export const getConfiguredLandingMenuId = (userRole, env = process.env) => {
  const varName = LANDING_ENV_VAR_BY_ROLE[userRole]
  if (!varName) return null
  const value = env[varName]
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null
}

// A TISOC CustomerAdmin may only land somewhere ThreatIntelCustomerRedirect would not
// immediately redirect away from — the same matchPath test that component runs.
const isAllowedForLicense = (path, userRole, licenseType) => {
  if (userRole !== 'CustomerAdmin' || licenseType !== THREAT_INTEL_CUST_LICENSE_CODE) return true
  return THREAT_INTEL_CUST_VALID_PATHS.some((validPath) =>
    matchPath(path, { path: validPath, exact: true, strict: false }),
  )
}

/**
 * Resolve the configured landing path for a persona, or null to fall back.
 *
 * Returns null — never throws, never navigates — whenever any guard fails, and warns
 * once so a misconfiguration is visible in the console rather than silent.
 */
export const resolveLandingPath = ({
  userRole,
  licenseType,
  selectedCustomer,
  isGlobalView = false,
  isSuperFeedView = false,
  env = process.env,
} = {}) => {
  const menuId = getConfiguredLandingMenuId(userRole, env)
  if (!menuId) return null

  const warn = (reason) =>
    // eslint-disable-next-line no-console
    console.warn(
      `[landingPage] Ignoring ${LANDING_ENV_VAR_BY_ROLE[userRole]}="${menuId}": ${reason}. ` +
        'Falling back to the default landing path.',
    )

  const entry = findMenuEntry(menuId)
  if (!entry) {
    warn('no such menuId in menuRegistry')
    return null
  }

  if (!entry.roles.includes(userRole)) {
    warn(`not permitted for role ${userRole}`)
    return null
  }

  // Customer-scoped templates are unresolvable without a selected customer, and a
  // '/customers/undefined/...' path would hit the catch-all and loop.
  const needsCustomer = entry.route.includes('customerId')
  if (needsCustomer && !selectedCustomer?.id) {
    warn('no customer is selected yet')
    return null
  }

  const path = buildMenuPath(entry, selectedCustomer?.id, isGlobalView)
  if (!path) {
    warn('the route template could not be resolved')
    return null
  }

  if (isGlobalView && !isPathAllowedInGlobalView(path)) {
    warn('that destination is disabled in Global View')
    return null
  }

  if (isSuperFeedView && !isPathAllowedInSuperFeed(path)) {
    warn('that destination is disabled in Super Feed view')
    return null
  }

  if (!isAllowedForLicense(path, userRole, licenseType)) {
    warn('that destination is not available on this customer\'s licence')
    return null
  }

  return path
}
